import React from 'react';
import { Box, Typography } from "@mui/material";
import BracketGameSweet16 from './BracketGameSweet16';
import AgnosticBracketChamp from './AgnosticBracketChamp';
import "./AgnosticSixteenTeamBracket.scss";
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import TiebreakerSingleInput from '../../GamePageComponents/Tiebreakers/TiebreakerSingleInput';
import AgnosticBracketGameMobile from './AgnosticBracketGameMobile';

const AgnosticSixteenTeamMobileBracket = ({
    regions,
    games,
    teams,
    league,
    handleGameClick,
    round1Ref,
    round2Ref,
    round3Ref,
    round4Ref,
    round5Ref,
    overrideTiebreaker,
    userTiebreaker,
    bracketState,
    seriesLengths,
    setSeriesLengths,
    lastRound,
    picksInfo,
    hasBracketDeadlinePassed,
    checkForCompleteRoundAndScroll,
    tiebreakerCount,
    pointsByRound,
}) => {
    //console.log("Bracket State", bracketState, "Series Lengths", seriesLengths, "Points By Round", pointsByRound);
    const {
        intervalInfo,
        masterPoolData,
    } = useUserAndGameContext();

    const firstFinalist = bracketState?.find(pick => pick.round === 5 && pick.bracket === `${regions?.topLeft}-${regions?.bottomLeft}`)?.teamId;
    const secondFinalist = bracketState?.find(pick => pick.round === 5 && pick.bracket === `${regions?.topRight}-${regions?.bottomRight}`)?.teamId;

    const usingSeriesLengths = masterPoolData?.gameParameters?.usingSeriesLengths;
    //console.log("Using Series Lengths:", usingSeriesLengths);

    return (
        <Box className="agnostic-regional-bracket-container" sx={{ marginTop: '50px'}}>
            <Box className="agnostic-regional-bracket">
                {/* First Round Column */}
                <Box className="agnostic-regional-bracket-column agnostic-regional-bracket-column-games-first">
                    <Box sx={{ width: '230px', marginBottom: '24px', paddingBottom: '30px', marginLeft: '50px' }}>
                        <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>{picksInfo[0]?.name}</Typography>
                        <Typography sx={{ fontSize: '18px', fontWeight: '700' }}>
                            {!hasBracketDeadlinePassed
                                ? `${picksInfo[0]?.points} Point${picksInfo[0]?.points === 1 ? '' : 's'}`
                                : `${pointsByRound[0]?.points || 0} Point${(pointsByRound[0]?.points || 0) === 1 ? '' : 's'}`
                            }
                        </Typography>
                    </Box>
                    <Box ref={round1Ref} >
                        {Array(8).fill(null).map((_, index) => (
                            <Box key={`round1-${index}`} className="agnostic-regional-bracket-game">
                                <AgnosticBracketGameMobile
                                    matchupId={index + 1}
                                    round={1}
                                    games={games}
                                    teams={teams} 
                                    league={league}
                                    handleGameClick={handleGameClick}
                                    hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                    bracketState={bracketState}
                                    seriesLengths={seriesLengths}
                                    setSeriesLengths={setSeriesLengths}
                                    hideUserPicksOutsideBoxRound={1}
                                    usingSeriesLengths={usingSeriesLengths}
                                    picksInfo={picksInfo}
                                    checkForCompleteRoundAndScroll={checkForCompleteRoundAndScroll}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* First Round Connectors */}
                <Box className="agnostic-regional-bracket-connector-column agnostic-first-round">
                    {Array(4).fill(null).map((_, index) => (
                        <Box 
                            key={`connector1-${index}`} 
                            className="agnostic-connector-wrapper agnostic-first-round-expanded"
                        >
                            <div className="agnostic-vertical-line" />
                        </Box>
                    ))}
                </Box>

                {/* Second Round Column */}
                <Box className="agnostic-regional-bracket-column agnostic-regional-bracket-column-games-second">
                    <Box sx={{ width: '230px', marginBottom: '84.5px', paddingBottom: '30px', marginLeft: '50px' }}>
                        <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>{picksInfo[1]?.name}</Typography>
                        <Typography sx={{ fontSize: '18px', fontWeight: '700' }}>
                            {!hasBracketDeadlinePassed
                                ? `${picksInfo[1]?.points} Point${picksInfo[1]?.points === 1 ? '' : 's'}`
                                : `${pointsByRound[1]?.points || 0} Point${(pointsByRound[1]?.points || 0) === 1 ? '' : 's'}`
                            }
                        </Typography>
                    </Box>
                    <Box ref={round2Ref}>
                        {Array(4).fill(null).map((_, index) => (
                            <React.Fragment key={`round2-${index}`}>
                                <Box className="agnostic-regional-bracket-game">
                                    <AgnosticBracketGameMobile
                                        matchupId={index + 9}
                                        round={2}
                                        games={games}
                                        teams={teams} 
                                        league={league}
                                        handleGameClick={handleGameClick}
                                        hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                        bracketState={bracketState}
                                        seriesLengths={seriesLengths}
                                        setSeriesLengths={setSeriesLengths}
                                        usingSeriesLengths={usingSeriesLengths}
                                        picksInfo={picksInfo}
                                        checkForCompleteRoundAndScroll={checkForCompleteRoundAndScroll}
                                    />
                                </Box>
                                <Box sx={{ height: '112px' }} />
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                {/* Second Round Connectors */}
                <Box className="agnostic-regional-bracket-connector-column agnostic-second-round">
                    {Array(2).fill(null).map((_, index) => (
                        <Box 
                            key={`connector2-${index}`} 
                            className="agnostic-connector-wrapper agnostic-second-round-expanded"
                        >
                            <div className="agnostic-vertical-line" />
                        </Box>
                    ))}
                </Box>

                {/* Sweet 16 Column */}
                <Box className="agnostic-regional-bracket-column agnostic-regional-bracket-column-games-third">
                    <Box sx={{ width: '230px', marginBottom: '24px', paddingBottom: '250px', marginLeft: '50px' }}>
                        <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>{picksInfo[2]?.name}</Typography>
                        <Typography sx={{ fontSize: '18px', fontWeight: '700' }}>
                            {!hasBracketDeadlinePassed
                                ? `${picksInfo[2]?.points} Point${picksInfo[2]?.points === 1 ? '' : 's'}`
                                : `${pointsByRound[2]?.points || 0} Point${(pointsByRound[2]?.points || 0) === 1 ? '' : 's'}`
                            }
                        </Typography>
                    </Box>
                    <Box ref={round3Ref}>
                        {Array(2).fill(null).map((_, index) => (
                            <React.Fragment key={`round3-${index}`}>
                                <Box className="agnostic-regional-bracket-game">
                                    <AgnosticBracketGameMobile
                                        matchupId={index + 13}
                                        round={3}
                                        games={games}
                                        teams={teams} 
                                        league={league}
                                        handleGameClick={handleGameClick}
                                        hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                        bracketState={bracketState}
                                        seriesLengths={seriesLengths}
                                        setSeriesLengths={setSeriesLengths}
                                        usingSeriesLengths={usingSeriesLengths}
                                        picksInfo={picksInfo}
                                        checkForCompleteRoundAndScroll={checkForCompleteRoundAndScroll}
                                    />
                                </Box>
                                <Box sx={{ height: '432.5px' }} />
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                {/* Sweet 16 Connectors */}
                <Box className="agnostic-regional-bracket-connector-column agnostic-sweet-16">
                    <Box className="agnostic-connector-wrapper agnostic-sweet-16-expanded">
                        <div className="agnostic-vertical-line" />
                    </Box>
                </Box>

                {/* Elite 8 Column */}
                <Box className="agnostic-regional-bracket-column agnostic-regional-bracket-column-games-final">
                    <Box sx={{ width: '230px', marginBottom: '24px', paddingBottom: '570.5px', marginLeft: '50px' }}>
                        <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>{picksInfo[3]?.name}</Typography>
                        <Typography sx={{ fontSize: '18px', fontWeight: '700' }}>
                            {!hasBracketDeadlinePassed
                                ? `${picksInfo[3]?.points} Point${picksInfo[3]?.points === 1 ? '' : 's'}`
                                : `${pointsByRound[3]?.points || 0} Point${(pointsByRound[3]?.points || 0) === 1 ? '' : 's'}`
                            }
                        </Typography>
                    </Box>
                    <Box ref={round4Ref}>
                        <Box className="agnostic-regional-bracket-game">
                            <AgnosticBracketGameMobile
                                matchupId={15}
                                round={4}
                                games={games}
                                teams={teams} 
                                league={league}
                                handleGameClick={handleGameClick}
                                hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                bracketState={bracketState}
                                seriesLengths={seriesLengths}
                                setSeriesLengths={setSeriesLengths}
                                usingSeriesLengths={usingSeriesLengths}
                                picksInfo={picksInfo}
                                checkForCompleteRoundAndScroll={checkForCompleteRoundAndScroll}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Champion Column */}
                <Box className="agnostic-regional-bracket-column agnostic-regional-bracket-column-champion" sx={{ marginLeft: '-6px'}}>
                    <Box sx={{ width: '230px', marginBottom: '24px', paddingBottom: '485px' }}>
                        <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>Champ</Typography>
                    </Box>
                    <Box ref={round5Ref}>
                        <AgnosticBracketChamp
                            matchupId={15}
                            round={4}
                            lastRound={lastRound}
                            games={games}
                            teams={teams} 
                            league={league}
                            bracketState={bracketState}
                            handleGameClick={handleGameClick}
                            hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                        />
                    </Box>
                    {tiebreakerCount > 0 && (
                        <Box sx={{ marginTop: '-60px' }}>
                            <TiebreakerSingleInput
                                firstFinalist={firstFinalist}
                                secondFinalist={secondFinalist}
                                league={league}
                                teams={teams}
                                hasDeadlinePassed={hasBracketDeadlinePassed}
                                overrideTiebreaker={overrideTiebreaker}
                                userTiebreaker={userTiebreaker}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default AgnosticSixteenTeamMobileBracket;